import type { UserRole } from '../types/api';

/**
 * Check if user has admin privileges (any admin role)
 */
export const isAdmin = (role?: string): boolean => {
  if (!role) return false;
  return role === 'ADMIN';
};

/**
 * Check if user has any level of admin access (including location-based admins)
 */
export const isAnyAdmin = (role?: string): boolean => {
  if (!role) return false;
  return role.includes('ADMIN');
};

/**
 * Check if user can manage facilities (all admin roles)
 */
export const canManageFacilities = (role?: UserRole): boolean => {
  return isAnyAdmin(role);
};

/**
 * Check if user can update all facility fields (all admin roles)
 * All admin roles can update all facility fields except location
 */
export const canUpdateAllFacilityFields = (role?: UserRole): boolean => {
  return isAnyAdmin(role);
};

/**
 * Check if user can manage facility counts (village and cell admins only)
 */
export const canManageFacilityCounts = (role?: UserRole): boolean => {
  if (!role) return false;
  return role === 'VILLAGE_ADMIN' || role === 'CELL_ADMIN';
};

/**
 * Check if user can manage facility counts for villages under their cell (cell admins only)
 */
export const canManageVillageFacilityCounts = (role?: UserRole): boolean => {
  if (!role) return false;
  return role === 'CELL_ADMIN';
};

/**
 * Get the maximum location level that an admin can manage
 */
export const getMaxManageableLevel = (role?: UserRole): string => {
  if (!role) return '';

  switch (role) {
    case 'VILLAGE_ADMIN':
      return 'village';
    case 'CELL_ADMIN':
      return 'village';
    case 'SECTOR_ADMIN':
      return 'village';
    case 'DISTRICT_ADMIN':
      return 'village';
    case 'PROVINCE_ADMIN':
      return 'village';
    case 'ADMIN':
      return 'village';
    default:
      return '';
  }
};

/**
 * Check if admin should use location selector (true for cell admin only)
 */
export const shouldUseLocationSelector = (role?: UserRole): boolean => {
  if (!role) return false;
  return role === 'CELL_ADMIN';
};

/**
 * Check if user can manage facility types (system admin only)
 */
export const canManageFacilityTypes = (role?: UserRole): boolean => {
  return isAdmin(role);
};

/**
 * Check if user can manage roles and permissions (system admin only)
 */
export const canManageRoles = (role?: UserRole): boolean => {
  return isAdmin(role);
};

/**
 * Check if user can manage users (all admin roles except VILLAGE_ADMIN can manage users in their jurisdiction)
 */
export const canManageUsers = (role?: string): boolean => {
  if (!role) return false;
  return isAnyAdmin(role) && role !== 'VILLAGE_ADMIN';
};

/**
 * Check if user can create assessments (all authenticated users)
 */
export const canCreateAssessment = (role?: UserRole): boolean => {
  return !!role; // All authenticated users can create assessments
};

/**
 * Check if user can view content (all roles)
 */
export const canView = (role?: UserRole): boolean => {
  return !!role;
};

/**
 * Check if user can export data (all authenticated users)
 */
export const canExportData = (role?: UserRole | string): boolean => {
  return !!role; // All authenticated users can export data
};

/**
 * Check if user can export data for a specific location
 * Users can only export data for locations within their assigned hierarchy
 */
export const canExportLocationData = (
  role?: UserRole,
  selectedLocationId?: string,
  userLocationHierarchy?: {
    assignedLocationId: string | null;
    provinceId: string | null;
    districtId: string | null;
    sectorId: string | null;
    cellId: string | null;
    villageId: string | null;
  }
): boolean => {
  if (!role || !canExportData(role)) return false;

  // ADMIN can export all locations
  if (role === 'ADMIN') return true;

  // If no location selected, users can export their accessible locations
  if (!selectedLocationId) return true;

  // If user has no assigned location, they cannot export location-specific data
  if (!userLocationHierarchy?.assignedLocationId) return false;

  // Users can export data for their assigned location or its children
  // This is a simplified check - the backend should validate this more strictly
  return true;
};

/**
 * Get user role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    ADMIN: 'System Administrator',
    PROVINCE_ADMIN: 'Province Administrator',
    DISTRICT_ADMIN: 'District Administrator',
    SECTOR_ADMIN: 'Sector Administrator',
    CELL_ADMIN: 'Cell Administrator',
    VILLAGE_ADMIN: 'Village Administrator',
  };
  return roleNames[role] || role;
};

// -----------------------------
// RBAC permission-code helpers
// -----------------------------
export const hasPermission = (
  user: { permissionCodes?: string[]; role?: unknown } | null | undefined,
  permissionCode: string
): boolean => {
  if (!user) return false;
  const codes = user.permissionCodes ?? [];
  if (codes.includes(permissionCode)) return true;

  // Fallback for legacy users without permissionCodes stored:
  // treat legacy ADMIN role as having everything (UI safeguard only).
  if (!codes.length && typeof user.role === 'string') {
    return user.role === 'ADMIN';
  }

  return false;
};

export const hasAnyPermission = (
  user: { permissionCodes?: string[]; role?: unknown } | null | undefined,
  permissionCodes: string[]
): boolean => {
  if (!user) return false;
  const codes = user.permissionCodes ?? [];
  if (permissionCodes.some(p => codes.includes(p))) return true;

  if (!codes.length && typeof user.role === 'string') {
    return user.role === 'ADMIN';
  }

  return false;
};

